/** The bell in the top bar: notifications of the current team (mentions, assignments, notes, status changes). */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useMeta } from './meta';
import { Avatar, Dropdown, issueNo, relTime } from './components';
import { t } from './i18n';

interface Item {
  id: number;
  kind: string;
  detail: { from?: number; to?: number; fields?: string[]; handler_id?: number | null; handler?: string | null; target?: number | null };
  created_at: string;
  read: boolean;
  note_id: number | null;
  issue_id: number;
  summary: string;
  actor: string | null;
  actor_name: string | null;
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path
        d="M12 3a6 6 0 0 0-6 6v3.6l-1.6 2.7A1 1 0 0 0 5.3 17h13.4a1 1 0 0 0 .9-1.7L18 12.6V9a6 6 0 0 0-6-6zm-2 15a2 2 0 0 0 4 0z"
        fill="currentColor"
      />
    </svg>
  );
}

export function Bell() {
  const { meta } = useMeta();
  const location = useLocation();
  const count = useQuery({
    queryKey: ['notif-count', meta.team?.id],
    queryFn: () => api.get<{ unread: number }>('/api/notifications/count'),
    refetchInterval: 60_000,
    enabled: !!meta.team,
  });
  // opening an issue marks its notifications as read on the server
  const { refetch } = count;
  useEffect(() => {
    const timer = setTimeout(() => void refetch(), 800);
    return () => clearTimeout(timer);
  }, [location.pathname, refetch]);
  if (!meta.team) return null;
  const unread = count.data?.unread ?? 0;
  return (
    <Dropdown
      align="right"
      trigger={(open) => (
        <button className={`nav-btn bell ${open ? 'open' : ''}`} title={t('Notifications')} aria-label={t('Notifications')}>
          <BellIcon />
          {unread > 0 && <span className="bell-badge">{unread > 99 ? '99+' : unread}</span>}
        </button>
      )}
    >
      {(close) => <BellPanel close={close} />}
    </Dropdown>
  );
}

function BellPanel({ close }: { close: () => void }) {
  const { meta, label } = useMeta();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['notifications', meta.team?.id],
    queryFn: () => api.get<{ unread: number; items: Item[] }>('/api/notifications'),
    staleTime: 0,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['notifications'] });
    qc.invalidateQueries({ queryKey: ['notif-count'] });
  };
  const text = (n: Item) => {
    const user = n.actor_name || n.actor || t('Someone');
    switch (n.kind) {
      case 'mention':
        return t('{user} mentioned you', { user });
      case 'new':
        return t('{user} reported a new issue', { user });
      case 'assigned':
        return n.detail.handler_id === meta.user.id
          ? t('{user} assigned the issue to you', { user })
          : t('{user} assigned the issue to {handler}', { user, handler: n.detail.handler ?? '' });
      case 'status':
        return t('{user} changed the status to {status}', { user, status: label('status', n.detail.to ?? 0) });
      case 'bugnote':
        return t('{user} added a note', { user });
      case 'priority':
        return t('{user} changed the priority', { user });
      case 'relation':
        return t('{user} added a relationship', { user });
      default:
        return t('{user} updated the issue', { user });
    }
  };
  return (
    <div className="bell-panel">
      <div className="bell-head">
        <strong>{t('Notifications')}</strong>
        {(data?.unread ?? 0) > 0 && (
          <button className="link" onClick={() => api.post('/api/notifications/read', { all: true }).then(refresh)}>
            {t('Mark all as read')}
          </button>
        )}
      </div>
      {isLoading && <p className="muted bell-empty">{t('Loading…')}</p>}
      {data && data.items.length === 0 && <p className="muted bell-empty">{t('No notifications yet.')}</p>}
      <div className="bell-list">
        {data?.items.map((n) => (
          <button
            key={n.id}
            className={`bell-item ${n.read ? '' : 'unread'}`}
            onClick={() => {
              close();
              if (!n.read) void api.post('/api/notifications/read', { ids: [n.id] }).then(refresh);
              navigate(`/issues/${n.issue_id}${n.note_id ? `#c${n.note_id}` : ''}`);
            }}
          >
            <Avatar name={n.actor_name || n.actor || '?'} size={28} />
            <span className="bell-text">
              <span>{text(n)}</span>
              <span className="muted small">
                {issueNo(n.issue_id)} {n.summary} · {relTime(n.created_at)}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
